'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ChevronLeft, Loader2, User, Mail, Phone, Car, FileText } from 'lucide-react';
import type { BookingState, CustomerInfo } from './bookingTypes';

interface Step3Props {
  booking: BookingState;
  onConfirm: (customer: CustomerInfo) => void;
  onBack: () => void;
}

type FormValues = CustomerInfo;

export default function Step3ContactForm({ booking, onConfirm, onBack }: Step3Props) {
  const [isGuest, setIsGuest] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { isGuest: true },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    // BACKEND INTEGRATION: POST /api/bookings with booking + customer data
    await new Promise((r) => setTimeout(r, 1800));
    onConfirm({ ...data, isGuest });
    setIsSubmitting(false);
  };

  return (
    <div className="animate-slide-up">
      {/* Guest vs Account Toggle */}
      <div className="flex gap-2 mb-6 bg-muted p-1 rounded-xl w-fit">
        <button
          onClick={() => setIsGuest(true)}
          className={`px-5 py-2 rounded-lg text-sm font-600 transition-all ${
            isGuest ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Continue as Guest
        </button>
        <button
          onClick={() => setIsGuest(false)}
          className={`px-5 py-2 rounded-lg text-sm font-600 transition-all ${
            !isGuest ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Sign In / Create Account
        </button>
      </div>

      {!isGuest && (
        <div className="bg-secondary rounded-xl p-4 mb-6 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <User size={15} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-600 text-foreground">Sign in to track your bookings</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Account holders get reminders, booking history, and easy rescheduling.{' '}
              <a href="/sign-up-login" className="text-primary font-600 hover:underline">
                Sign in here →
              </a>
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Personal Info Section */}
        <div className="bg-card rounded-xl border border-border shadow-card p-6 mb-5">
          <div className="flex items-center gap-2 mb-5">
            <User size={16} className="text-primary" />
            <h3 className="text-sm font-700 text-foreground">Personal Information</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                {...register('firstName', { required: 'First name is required' })}
                placeholder="James"
                className={`input-field ${errors.firstName ? 'error' : ''}`}
              />
              {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                {...register('lastName', { required: 'Last name is required' })}
                placeholder="Okafor"
                className={`input-field ${errors.lastName ? 'error' : ''}`}
              />
              {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5">
                <Mail size={12} className="inline mr-1 text-muted-foreground" />
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
                placeholder="james.okafor@gmail.com"
                className={`input-field ${errors.email ? 'error' : ''}`}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
              <p className="text-xs text-muted-foreground mt-1">Confirmation and reminders will be sent here.</p>
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5">
                <Phone size={12} className="inline mr-1 text-muted-foreground" />
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                {...register('phone', {
                  required: 'Phone number is required',
                  pattern: { value: /^\+?[\d\s\-()]{7,15}$/, message: 'Enter a valid phone number' },
                })}
                placeholder="(312) 555-0182"
                className={`input-field ${errors.phone ? 'error' : ''}`}
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
            </div>
          </div>
        </div>

        {/* Vehicle Info Section */}
        <div className="bg-card rounded-xl border border-border shadow-card p-6 mb-5">
          <div className="flex items-center gap-2 mb-5">
            <Car size={16} className="text-primary" />
            <h3 className="text-sm font-700 text-foreground">Vehicle Information</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5">
                Year <span className="text-red-500">*</span>
              </label>
              <input
                {...register('vehicleYear', { required: 'Year is required' })}
                placeholder="2021"
                className={`input-field ${errors.vehicleYear ? 'error' : ''}`}
              />
              {errors.vehicleYear && <p className="text-red-500 text-xs mt-1">{errors.vehicleYear.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5">
                Make <span className="text-red-500">*</span>
              </label>
              <input
                {...register('vehicleMake', { required: 'Make is required' })}
                placeholder="Toyota"
                className={`input-field ${errors.vehicleMake ? 'error' : ''}`}
              />
              {errors.vehicleMake && <p className="text-red-500 text-xs mt-1">{errors.vehicleMake.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5">
                Model <span className="text-red-500">*</span>
              </label>
              <input
                {...register('vehicleModel', { required: 'Model is required' })}
                placeholder="Camry"
                className={`input-field ${errors.vehicleModel ? 'error' : ''}`}
              />
              {errors.vehicleModel && <p className="text-red-500 text-xs mt-1">{errors.vehicleModel.message}</p>}
            </div>
          </div>
        </div>

        {/* Notes Section */}
        <div className="bg-card rounded-xl border border-border shadow-card p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={16} className="text-primary" />
            <h3 className="text-sm font-700 text-foreground">Special Requests</h3>
            <span className="text-xs text-muted-foreground font-400">(optional)</span>
          </div>
          <textarea
            {...register('notes')}
            rows={3}
            placeholder="E.g. Pet hair removal needed, please use fragrance-free products, gate code is #1234..."
            className="input-field resize-none"
          />
        </div>

        {/* Required fields note */}
        <p className="text-xs text-muted-foreground mb-4">
          <span className="text-red-500">*</span> Required fields
        </p>

        {/* Navigation */}
        <div className="flex justify-between">
          <button type="button" onClick={onBack} className="btn-secondary flex items-center gap-2">
            <ChevronLeft size={16} />
            Back
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary px-8 flex items-center gap-2 min-w-[180px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Confirming Booking...
              </>
            ) : (
              'Confirm Booking'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}